import React from "react";
import { smallImageUrl, thumbUrl } from "../../../components/aotd-gallery/galleryImages";
import { useGetAotdByNumberQuery } from "../printsApi";
import type { AotdDay } from "../printsApi";
import { FORMATS } from "../printProducts";
import type { PrintEdition, PrintFormat } from "../printProducts";
import { CardFace } from "./faces/CardFace";
import { SheetFace } from "./faces/SheetFace";
import { MockupScene } from "./MockupScene";
import { SCENES } from "./scenes";
import type { SceneName } from "./scenes";
import "./printShowcase.css";

// How one print of a day looks for real: its print face (the same layout as the print file) in a
// mockup photo, a wall, a shelf, a hand, a binder. Collection scenes fill their other spots with
// the days before it. The prints page's gallery (today/PrintGallery) shows them one by one.

const CARD_MM = 5;                       // 315 x 440 px
const SHEET_PX = { single: 900, collection: 520 };

/** The days that fill a collection scene: the ones before it, then after it near the first day. */
function neighbours(day: AotdDay, count: number): number[] {
  const out: number[] = [];
  for (let n = day.number - 1; n >= 1 && out.length < count; n--) out.push(n);
  for (let n = day.number + 1; n <= day.latestNumber && out.length < count; n++) out.push(n);
  return out;
}

function faceSize(format: PrintFormat, collection: boolean): [number, number] {
  if (format === "trading-card") return [63 * CARD_MM, 88 * CARD_MM];
  const w = collection ? SHEET_PX.collection : SHEET_PX.single;
  return [w, w];
}

const Face: React.FC<{ day: AotdDay; format: PrintFormat; edition: PrintEdition; collection: boolean }> = ({ day, format, edition, collection }) => {
  const art = collection ? thumbUrl(day.filename) : smallImageUrl(day.filename);
  if (format === "trading-card") return <CardFace day={day} art={art} edition={edition} mm={CARD_MM} />;
  const [w] = faceSize(format, collection);
  return <SheetFace day={day} art={art} size={format === "poster" ? 50 : 21} edition={edition} width={w} />;
};

/** Another day in a collection scene; plain paper until it loads or when it fails. */
const OtherDay: React.FC<{ number: number; format: PrintFormat }> = ({ number, format }) => {
  const { data } = useGetAotdByNumberQuery(number);
  if (!data) {
    const [w, h] = faceSize(format, true);
    return <div className="ps-paper" style={{ width: w, height: h }} />;
  }
  return <Face day={data} format={format} edition="print" collection />;
};

/** One scene of a day's print, with the days before it in a collection scene's other spots. */
export const ShowcaseScene: React.FC<{ day: AotdDay; format: PrintFormat; edition: PrintEdition; scene: SceneName }> = ({
  day,
  format,
  edition,
  scene,
}) => {
  const spots = SCENES[scene].targets.length;
  const collection = spots > 1;
  const name = FORMATS.find((f) => f.format === format)?.name ?? format;
  const faces = [
    <Face key={day.number} day={day} format={format} edition={edition} collection={collection} />,
    ...(collection ? neighbours(day, spots - 1).map((n) => <OtherDay key={n} number={n} format={format} />) : []),
  ];
  const label = `${edition === "original" ? "original " : ""}${name} of #${day.number} ${day.title}, ${SCENES[scene].label}`;
  return <MockupScene scene={scene} faces={faces} faceSize={faceSize(format, collection)} label={label} />;
};

export default ShowcaseScene;
